// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { useCallback, useEffect, useState } from "react";
import type { Coords } from "@/lib/geo";

export type GeoStatus = "idle" | "prompting" | "granted" | "denied" | "unavailable" | "insecure" | "error";

const STORAGE_KEY = "kc-geo-consent";

interface GeolocationState {
  coords: Coords | null;
  status: GeoStatus;
  /** Trigger a (re)locate. Safe to call from a click handler. */
  request: () => void;
  /** Forget the user location + stored consent. */
  clear: () => void;
}

/**
 * Thin wrapper around the browser Geolocation API tuned for "find karaoke near
 * me". We never auto-prompt on load (that's hostile) — the caller wires
 * `request()` to an explicit "Near me" tap. Once granted, we remember the
 * consent in localStorage so a returning user is re-located silently.
 */
export function useGeolocation(): GeolocationState {
  const [coords, setCoords] = useState<Coords | null>(null);
  const [status, setStatus] = useState<GeoStatus>("idle");

  const request = useCallback(() => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      setStatus("unavailable");
      return;
    }
    // The Geolocation API only works on secure origins (https:// or localhost).
    // On plain http:// (e.g. an IP/LAN dev URL) the call is silently refused by
    // the browser, so surface it as "unavailable" with a clear reason instead
    // of leaving the user staring at a pill that does nothing.
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setStatus("insecure");
      return;
    }
    setStatus("prompting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setStatus("granted");
        try {
          localStorage.setItem(STORAGE_KEY, "1");
        } catch {
          /* private mode — non-fatal */
        }
      },
      (err) => {
        setStatus(err.code === err.PERMISSION_DENIED ? "denied" : "error");
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    );
  }, []);

  const clear = useCallback(() => {
    setCoords(null);
    setStatus("idle");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // Returning user who already consented: re-locate silently on mount.
  useEffect(() => {
    let consented = false;
    try {
      consented = localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (consented) request();
  }, [request]);

  return { coords, status, request, clear };
}
